import audioBookFolders from '../constants/audioBookFolders';

const timingBaseUrl = String(
  process.env.REACT_APP_AUDIO_TIMINGS_BASE_URL || '/audio-timings'
).replace(/\/$/, '');

const cache = new Map();

const storageKey = (book, chapter) => 'ctb:audio-verse-timings:' + book + ':' + chapter;

const normaliseTimings = value => {
  const source = Array.isArray(value) ? value : value?.timings;
  if (!Array.isArray(source)) return [];

  return source
    .map(item => ({
      verse: Number(item?.verse),
      start: Number(item?.start),
    }))
    .filter(item => Number.isInteger(item.verse) && item.verse > 0 && Number.isFinite(item.start) && item.start >= 0)
    .sort((left, right) => left.start - right.start);
};

const readSavedTimings = (book, chapter) => {
  try {
    return normaliseTimings(JSON.parse(window.localStorage.getItem(storageKey(book, chapter)) || '[]'));
  } catch {
    return [];
  }
};

export const loadVerseAudioTimings = async ({ book, chapter }) => {
  const folder = audioBookFolders[book];
  if (!folder || !timingBaseUrl) return [];

  const cacheKey = book + ':' + chapter;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  const request = (async () => {
    try {
      const serverResponse = await fetch(
        '/api/bible-audio-timings?book=' + encodeURIComponent(book) + '&chapter=' + encodeURIComponent(chapter),
        { cache: 'no-store' }
      );
      if (serverResponse.ok) {
        const serverTimings = normaliseTimings(await serverResponse.json());
        if (serverTimings.length) return serverTimings;
      }
    } catch {
      // The static/local fallbacks keep audio sync available while the API is offline.
    }

    const saved = readSavedTimings(book, chapter);
    if (saved.length) return saved;

    const timingFile = folder + '/' + String(chapter).padStart(3, '0') + '.json';
    try {
      const staticResponse = await fetch(timingBaseUrl + '/' + timingFile, { cache: 'no-store' });
      return staticResponse.ok ? normaliseTimings(await staticResponse.json()) : [];
    } catch {
      return [];
    }
  })();

  cache.set(cacheKey, request);
  return request;
};

export const saveVerseAudioTimings = ({ book, chapter, timings }) => {
  const normalised = normaliseTimings(timings);
  window.localStorage.setItem(storageKey(book, chapter), JSON.stringify(normalised));
  cache.delete(book + ':' + chapter);
  return normalised;
};

export const saveVerseAudioTimingsToServer = async ({ book, chapter, timings, token }) => {
  const normalised = normaliseTimings(timings);
  const response = await fetch('/api/bible-audio-timings', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify({ book, chapter: Number(chapter), timings: normalised }),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result?.message || '保存逐節標注失敗。');
  }

  const saved = normaliseTimings(result);
  window.localStorage.setItem(storageKey(book, chapter), JSON.stringify(saved));
  cache.delete(book + ':' + chapter);
  return saved;
};

export const createVerseTimingFile = ({ book, chapter, timings }) => JSON.stringify({
  book,
  chapter: Number(chapter),
  timings: normaliseTimings(timings),
}, null, 2);

export { normaliseTimings };
